import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { LoaderCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

type Variant = 'accent' | 'buy' | 'sell' | 'ghost' | 'outline' | 'subtle' | 'danger';
type Size = 'xs' | 'sm' | 'md' | 'lg';

const VARIANT: Record<Variant, string> = {
  accent: 'bg-accent text-on-accent hover:bg-accent-hover font-semibold',
  buy: 'bg-buy text-on-buy hover:bg-buy-hover font-semibold',
  sell: 'bg-sell text-on-sell hover:bg-sell-hover font-semibold',
  ghost: 'text-fg-2 hover:bg-hover hover:text-fg',
  outline: 'border border-line-2 text-fg-2 hover:bg-hover hover:text-fg',
  subtle: 'bg-panel-2 text-fg-2 hover:bg-hover hover:text-fg',
  danger: 'bg-sell/15 text-sell hover:bg-sell/25',
};

const SIZE: Record<Size, string> = {
  xs: 'h-6 px-2 text-2xs gap-1 rounded-sm',
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-md',
  md: 'h-9 px-3.5 text-sm gap-2 rounded-md',
  lg: 'h-11 px-4 text-base gap-2 rounded-lg',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({ variant = 'subtle', size = 'md', loading, className, children, disabled, type = 'button', ...rest }, ref) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex shrink-0 items-center justify-center whitespace-nowrap transition-colors select-none disabled:cursor-not-allowed disabled:opacity-45',
        VARIANT[variant],
        SIZE[size],
        className,
      )}
      {...rest}
    >
      {loading && <LoaderCircle className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

export const IconButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { size?: 'sm' | 'md'; active?: boolean }>(function IconButton(
  { className, size = 'md', active, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md text-fg-3 transition-colors hover:bg-hover hover:text-fg disabled:opacity-40',
        size === 'sm' ? 'size-7' : 'size-9',
        active && 'bg-hover text-fg',
        className,
      )}
      {...rest}
    />
  );
});
